import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ShipperUpgradeNowButton } from "components/Shipper/components/ShipperUpgradeNowButton";
import { useSubscriptionStatus } from "hooks";

interface Props {
  loading: boolean;
  loadCompanyMembersError: UIError;
  companyMembers: CompanyMember[];
  selectedCompanyMembers: number[];
  setShareeRoleIds: (roleIds: number[]) => void;
  onClose: VoidFunction;
  isProject: boolean;
  autoFocus?: boolean;
}

export function ShareShipmentMenu(props: Props) {
  const {
    loading,
    loadCompanyMembersError,
    companyMembers,
    selectedCompanyMembers,
    setShareeRoleIds,
    onClose,
    isProject,
    autoFocus = false,
  } = props;

  const { subscription } = useSubscriptionStatus();
  if (subscription == null) {
    return null;
  }

  const isFreeTier = subscription.subscription_plan === "SHIPPER_FREE_TIER";

  const getCompanyMemberLabel = (member: CompanyMember) =>
    `${member.user.first_name ?? ""} ${member.user.last_name ?? ""}`;

  const selectedMembers = companyMembers.filter((m) =>
    selectedCompanyMembers.includes(m.id),
  );

  return (
    <Stack spacing={1}>
      <LoadingWrappedNode loading={loading}>
        <Autocomplete
          multiple
          options={companyMembers}
          getOptionLabel={getCompanyMemberLabel}
          value={selectedMembers}
          onChange={(_, newValue) =>
            setShareeRoleIds(newValue.map((m) => m.id))
          }
          disabled={isFreeTier}
          disableClearable
          forcePopupIcon={false}
          openOnFocus
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((member, index) => (
              <Chip
                label={getCompanyMemberLabel(member)}
                {...getTagProps({ index })}
                key={member.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={autoFocus}
              placeholder={
                selectedCompanyMembers.length === 0
                  ? `Who on your team can manage this ${isProject ? "project" : "shipment"}?`
                  : ""
              }
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <Tooltip title="Close Share with Teammates">
                    <IconButton
                      size="small"
                      onClick={onClose}
                      aria-label="Close Share with Teammates"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          )}
        />
        <ErrorTypography error={loadCompanyMembersError} />
      </LoadingWrappedNode>
      {isFreeTier && (
        <ShipperUpgradeNowButton
          buttonSize="small"
          trackingSource="create_shipment_form:shipper_sharee_paywall"
        />
      )}
    </Stack>
  );
}
